import { useState } from 'react';
import {
  Box, Button, Chip, Stack, TextField, Typography, alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ESTIMADO, ETIQUETA_CAMPO } from 'src/components/controlObra/useTemasDelCopiloto';

/**
 * Lo que el copiloto propone, dicho adentro del hilo.
 *
 * No es un formulario al costado de la conversación: es un turno de la
 * conversación que además trae números y botones. La estructura no es adorno —
 * el conteo, la plata y el antes→después los calcula el código y los dibuja
 * esto, así que no pasan por el modelo; y el botón es un sí de la persona, no
 * una interpretación de lo que quiso decir. Es lo único que hace que aceptar 76
 * tareas de un click no sea temerario.
 */

const fmt = (n) => `$ ${Math.round(Number(n) || 0).toLocaleString('es-AR')}`;

function mostrarValor(campo, v) {
  if (v == null || v === '') return '—';
  if (campo === 'fecha_fin' || campo === 'fecha_inicio') {
    return new Date(v).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  // "Propia" es como la llama el resto de la app (ver Contratos masivo). "Por
  // administración" es la jerga del rubro y nadie la reconoce en un botón.
  if (campo === 'ejecucion') return v === 'administracion' ? 'con gente propia' : 'contratada';
  return fmt(v);
}

/** "3 de Preliminares", "12 de Albañilería y 4 más" — contado por el código. */
function porRubro(balde) {
  if (!balde?.rubros?.length) return null;
  const [primero, ...resto] = balde.rubros;
  const extra = resto.reduce((a, r) => a + r.n, 0);
  return `${primero.n} de ${primero.nombre}${extra ? ` y ${extra} más` : ''}`;
}

export default function TarjetaTema({ q }) {
  const {
    temas, t, tema, enDetalle, iDetalle, actual, trabajando, error,
    setT, setDetalle, resolverTema, resolverUna,
    reparto, confirmarReparto, descartarReparto,
  } = q;
  const [corrigiendo, setCorrigiendo] = useState(null);

  if (!tema) return null;
  const n = tema.items.length;

  const cabecera = (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
      {temas.length > 1 && (
        <Chip size="small" variant="outlined" sx={{ height: 19, fontSize: 11 }}
          label={`Tema ${t + 1} de ${temas.length}`} />
      )}
      {/* Un número estimado y uno deducido de un contrato firmado no valen lo
          mismo, y la diferencia tiene que estar ANTES de apretar Aceptar. */}
      {ESTIMADO.includes(tema.origen) && (
        <Chip size="small" variant="outlined" color="warning" sx={{ height: 19, fontSize: 11 }} label="estimado" />
      )}
      {tema.monto > 0 && (
        <Typography variant="caption" color="text.secondary">{fmt(tema.monto)} en juego</Typography>
      )}
    </Stack>
  );

  const marco = (hijos) => (
    <Box sx={{
      alignSelf: 'flex-start',
      maxWidth: '100%',
      px: 1.5, py: 1.25, borderRadius: 1.5,
      border: '1px solid', borderColor: 'primary.light',
      bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
    }}>
      {hijos}
      {error && <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75 }}>{error}</Typography>}
    </Box>
  );

  // ── La fila, una por una ────────────────────────────────────────────────────
  if (enDetalle) {
    const etiqueta = ETIQUETA_CAMPO[actual.campo] || actual.campo;
    return marco(
      <>
        {cabecera}
        {n > 1 && (
          <Button
            size="small" color="inherit" startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={() => setDetalle(null)}
            sx={{ mb: 0.5, ml: -0.5, textTransform: 'none', fontSize: 12, opacity: 0.8 }}
          >
            Volver al tema ({iDetalle + 1} de {n})
          </Button>
        )}

        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {actual.destino?.nodo_nombre} — falta {etiqueta}
        </Typography>
        {actual.fundamento && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {actual.fundamento}
          </Typography>
        )}

        <Typography variant="body2" sx={{ mt: 0.75 }}>
          <Box component="span" sx={{ color: 'text.secondary' }}>{mostrarValor(actual.campo, actual.valor_actual)}</Box>
          {' → '}
          <Box component="span" sx={{ fontWeight: 700, color: 'primary.dark' }}>
            {mostrarValor(actual.campo, actual.valor_propuesto)}
          </Box>
        </Typography>

        {corrigiendo != null ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small" autoFocus fullWidth value={corrigiendo}
              onChange={(e) => setCorrigiendo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { resolverUna('corregida', corrigiendo); setCorrigiendo(null); }
              }}
              placeholder="El valor que va"
            />
            <Button size="small" variant="contained" disabled={trabajando || !String(corrigiendo).trim()}
              onClick={() => { resolverUna('corregida', corrigiendo); setCorrigiendo(null); }}>Guardar</Button>
            <Button size="small" color="inherit" onClick={() => setCorrigiendo(null)}>✕</Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Button size="small" variant="contained" disabled={trabajando}
              onClick={() => resolverUna('aceptada')} sx={{ textTransform: 'none' }}>
              Aceptar
            </Button>
            <Button size="small" color="inherit" variant="outlined" disabled={trabajando}
              onClick={() => setCorrigiendo(actual.valor_propuesto ?? '')} sx={{ textTransform: 'none' }}>
              Otro valor
            </Button>
            {/* "Ahora no" pasa a la siguiente sin cerrar nada; "No sé todavía"
                cierra el tema hasta que ese dato cambie. Son distintas a propósito:
                posponer es del momento, no saber es del dato. */}
            {n > 1 && (
              <Button size="small" color="inherit" disabled={trabajando}
                onClick={() => setDetalle((x) => ((x ?? 0) + 1) % n)} sx={{ textTransform: 'none' }}>
                Ahora no
              </Button>
            )}
            <Button size="small" color="inherit" disabled={trabajando}
              onClick={() => resolverUna('no_se')} sx={{ textTransform: 'none' }}>
              No sé todavía
            </Button>
          </Stack>
        )}
      </>,
    );
  }

  // ── Lo que entendí de tu respuesta, antes de escribir nada ──────────────────
  if (reparto) {
    const fila = (icono, color, balde, texto) => (balde.cuantas > 0 ? (
      <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
        <Box component="span" sx={{ color, fontWeight: 700, width: 14, flexShrink: 0 }}>{icono}</Box>
        <Typography variant="body2">
          <b>{balde.cuantas}</b> {texto}
          {porRubro(balde) && (
            <Box component="span" sx={{ color: 'text.secondary' }}> — {porRubro(balde)}</Box>
          )}
        </Typography>
      </Stack>
    ) : null);

    return marco(
      <>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Entendí esto:</Typography>
        {fila('✓', 'success.main', reparto.aplica, tema.copy.enPocas || 'les corresponde')}
        {fila('✗', 'text.secondary', reparto.no_aplica, 'no, quedan como están')}
        {fila('?', 'warning.main', reparto.dudo, 'no las pude ubicar')}
        {reparto.repregunta && (
          <Typography variant="body2" sx={{ mt: 1 }}>{reparto.repregunta}</Typography>
        )}
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
          {/* Una respuesta que sólo EXCLUYE igual resuelve algo: esas tareas
              dejan de preguntarse. Con el botón atado a `aplica` no había forma
              de confirmar "ninguna de éstas". */}
          <Button
            size="small" variant="contained" onClick={confirmarReparto}
            disabled={trabajando || (!reparto.aplica.cuantas && !reparto.no_aplica.cuantas)}
            sx={{ textTransform: 'none' }}
          >
            {reparto.aplica.cuantas ? 'Aplicar' : 'Confirmar'}
          </Button>
          <Button size="small" color="inherit" variant="outlined" disabled={trabajando}
            onClick={descartarReparto} sx={{ textTransform: 'none' }}>
            No es eso
          </Button>
        </Stack>
      </>,
    );
  }

  // ── El tema ─────────────────────────────────────────────────────────────────
  const { copy } = tema;
  return marco(
    <>
      {cabecera}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{copy.titulo(n)}</Typography>
      {/* El tema entero como segundo argumento: hay copys que necesitan lo que
          dice la propuesta —no una frase fija— para decir algo. */}
      {copy.detalle(n, tema) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {copy.detalle(n, tema)}
        </Typography>
      )}
      {copy.pregunta(n) && (
        <Typography variant="body2" sx={{ mt: 0.75 }}>{copy.pregunta(n)}</Typography>
      )}

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {/* Una oferta no tiene botón de aceptar: no trae un valor que escribir.
            Lo que queda es leerla y seguir, o decir que así está bien. */}
        {!copy.sinAceptar && (
          <Button size="small" variant="contained" disabled={trabajando}
            onClick={() => resolverTema('aceptada')} sx={{ textTransform: 'none' }}>
            {copy.aceptar(n)}
          </Button>
        )}
        {!copy.sinFilas && (
          <Button size="small" color="inherit" variant="outlined" disabled={trabajando}
            onClick={() => setDetalle(0)} sx={{ textTransform: 'none' }}>
            Ver una por una
          </Button>
        )}
        {temas.length > 1 && (
          <Button size="small" color="inherit" disabled={trabajando}
            onClick={() => setT((x) => (x + 1) % temas.length)} sx={{ textTransform: 'none' }}>
            Ahora no
          </Button>
        )}
        <Button size="small" color="inherit" disabled={trabajando}
          onClick={() => resolverTema('no_se')} sx={{ textTransform: 'none' }}>
          {copy.negar ? copy.negar(n) : 'No sé todavía'}
        </Button>
      </Stack>

      {/* La salida que los botones no tienen: el grupo casi nunca es uniforme. */}
      {!copy.sinFilas && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          O contestame abajo en tus palabras — "la mayoría sí, pero los preliminares van con proveedor".
        </Typography>
      )}
    </>,
  );
}
